import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Button } from '@tanium/react-button';
import { Box } from '@tanium/react-box';
import { Link } from '@tanium/react-link';
import { Stack } from '@tanium/react-stack';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { PortalHeading } from './PortalHeading';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { ErrorText } from './ErrorText';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { parseApiErrorMsg, ApiError } from '../../hooks/useApiErrorMsgParser';
import { isValidEmail } from '../../utilities/emailHelper';
import { useComplianceFramework } from '../../hooks/useComplianceFramework';
import { ComplianceFramework } from '../../types';
import { getCrossCloudAmpUrl } from '../../utilities/instanceHelper';
import { crossTierBrokerURL } from '../../auth/broker';

const localAccountExtension = '.mytanium.com';

// A relying party may name the user it wants signed in, which the broker passes
// through to this page as login_hint (OIDC Core 3.1.2.1). It only seeds the
// field the user can still edit, and the input is lowercased the same way typing
// is.
const loginHint = (): string => new URLSearchParams(window.location.search).get('login_hint')?.toLowerCase() ?? '';

export interface LoginFormProps {
  onSignIn: (username: string) => Promise<void>;
  onSignInIdp: (email: string) => Promise<void>;
  switchToForgotUsername: () => void;
}

export const LoginForm: React.VFC<LoginFormProps> = ({ onSignIn, onSignInIdp, switchToForgotUsername }) => {
  const { t } = useTranslation();
  const isGov = useComplianceFramework() === ComplianceFramework.USGov;
  const crossTierLabel = isGov ? t('Tanium Account (Commercial) login') : t('Tanium Account for U.S. Government login');
  const brokerUrl = crossTierBrokerURL(isGov);
  const crossTierUrl = brokerUrl ?? getCrossCloudAmpUrl();

  // A broker flow's link moves the app's login, so a second plain click during
  // the first navigation is dropped. A modified click (new tab) arms nothing,
  // and a page restored from the back-forward cache re-arms the link.
  const followed = useRef(false);
  useEffect(() => {
    const rearm = (event: PageTransitionEvent) => {
      if (event.persisted) {
        followed.current = false;
      }
    };
    window.addEventListener('pageshow', rearm);
    return () => window.removeEventListener('pageshow', rearm);
  }, []);
  const onCrossTierClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      if (followed.current) {
        event.preventDefault();
        return;
      }
      followed.current = true;
    },
    []
  );

  const [username, setUsername] = useState(loginHint);
  const { status, error, execute } = useAsync(() => {
    if (username === '') {
      throw new Error(t('Username is required'));
    }
    if (!isValidEmail(username)) {
      throw new Error(t('Invalid username'));
    }
    if (username.endsWith(localAccountExtension)) {
      return onSignIn(username).catch((err: ApiError) => {
        if (err.response?.status === 404) {
          throw new Error(t('User not found'));
        }
        throw new Error(parseApiErrorMsg(t, err));
      });
    }
    return onSignInIdp(username).catch((err: ApiError) => {
      if (err.response?.status === 404) {
        throw new Error(t('User not found'));
      }
      throw new Error(parseApiErrorMsg(t, err));
    });
  });

  const handleUsernameChange = useOnInputChange((uname: string) => setUsername(uname.toLowerCase()));

  const handleSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      event.preventDefault();
      await execute();
    },
    [execute]
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box style={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box width="100%">
          <PortalHeading />
          <form onSubmit={handleSubmit}>
            <FormRow
              label={t('Email or username')}
              description={t('Local Users have usernames ending in @[workspace].mytanium.com')}
              dataKey="username"
            >
              <LoginFormTextInput value={username} onChange={handleUsernameChange} required />
            </FormRow>
            {error && <ErrorText>{error.message}</ErrorText>}
            <Stack itemSpacing="2" justifyContent="left">
              <LoginFormButton loading={status === AsyncStatus.Pending}>{t('Sign In')} </LoginFormButton>
            </Stack>
          </form>
        </Box>
      </Box>
      <Stack
        direction="column"
        itemSpacing={1}
        justifyContent="center"
        alignItems="center"
        style={{ margin: 'auto 0 10px 0' }}
      >
        <Button variant="link" onClick={switchToForgotUsername}>
          {t('Login Help')}
        </Button>
        <Link href={crossTierUrl} onClick={brokerUrl === null ? undefined : onCrossTierClick}>
          {crossTierLabel}
        </Link>
      </Stack>
    </Box>
  );
};
